import { z } from "zod/v4";

export enum DomesticPaymentProvider {
	WeChatPay = "wechatpay",
	Alipay = "alipay",
}

export enum DomesticPaymentIntentStatus {
	Created = "created",
	RequiresAction = "requires_action",
	Processing = "processing",
	Paid = "paid",
	Fulfilled = "fulfilled",
	Expired = "expired",
	Closed = "closed",
	Failed = "failed",
	Refunding = "refunding",
	Refunded = "refunded",
	Unknown = "unknown",
}

export enum DomesticPaymentEventType {
	Created = "created",
	ProviderActionCreated = "provider_action_created",
	WebhookPaid = "webhook_paid",
	QueryPaid = "query_paid",
	WebhookClosed = "webhook_closed",
	QueryClosed = "query_closed",
	RefundSucceeded = "refund_succeeded",
	RefundFailed = "refund_failed",
	FulfillmentSucceeded = "fulfillment_succeeded",
	FulfillmentFailed = "fulfillment_failed",
	Unknown = "unknown",
}

export enum DomesticPaymentFulfillmentStatus {
	Pending = "pending",
	Running = "running",
	Succeeded = "succeeded",
	Failed = "failed",
	Skipped = "skipped",
}

export const WeChatJsapiPayloadSchema = z.object({
	appId: z.string(),
	timeStamp: z.string(),
	nonceStr: z.string(),
	package: z.string(),
	signType: z.string(),
	paySign: z.string(),
});

export const WeChatAppPayloadSchema = z.object({
	appid: z.string(),
	partnerid: z.string(),
	prepayid: z.string(),
	package: z.string(),
	noncestr: z.string(),
	timestamp: z.string(),
	sign: z.string(),
});

export const DomesticPaymentProviderActionSchema = z.discriminatedUnion(
	"provider",
	[
		z.discriminatedUnion("action", [
			z.object({
				provider: z.literal(DomesticPaymentProvider.WeChatPay),
				action: z.literal("qr_code"),
				code_url: z.string().min(1),
				expires_at: z.number().int().safe().nonnegative().optional(),
			}),
			z.object({
				provider: z.literal(DomesticPaymentProvider.WeChatPay),
				action: z.literal("h5_redirect"),
				url: z.string().url(),
			}),
			z.object({
				provider: z.literal(DomesticPaymentProvider.WeChatPay),
				action: z.literal("jsapi"),
				payload: WeChatJsapiPayloadSchema,
			}),
			z.object({
				provider: z.literal(DomesticPaymentProvider.WeChatPay),
				action: z.literal("app_payload"),
				payload: WeChatAppPayloadSchema,
			}),
		]),
		z.discriminatedUnion("action", [
			z.object({
				provider: z.literal(DomesticPaymentProvider.Alipay),
				action: z.literal("redirect"),
				url: z.string().url(),
			}),
			z.object({
				provider: z.literal(DomesticPaymentProvider.Alipay),
				action: z.literal("form_post"),
				html: z.string().min(1),
			}),
			z.object({
				provider: z.literal(DomesticPaymentProvider.Alipay),
				action: z.literal("qr_code"),
				qr_code: z.string().min(1),
				expires_at: z.number().int().safe().nonnegative().optional(),
			}),
			z.object({
				provider: z.literal(DomesticPaymentProvider.Alipay),
				action: z.literal("app_payload"),
				order_string: z.string().min(1),
			}),
		]),
	],
);

const allowedDomesticPaymentIntentTransitions: Record<
	DomesticPaymentIntentStatus,
	ReadonlySet<DomesticPaymentIntentStatus>
> = {
	[DomesticPaymentIntentStatus.Created]: new Set([
		DomesticPaymentIntentStatus.RequiresAction,
		DomesticPaymentIntentStatus.Expired,
		DomesticPaymentIntentStatus.Closed,
		DomesticPaymentIntentStatus.Failed,
		DomesticPaymentIntentStatus.Unknown,
	]),
	[DomesticPaymentIntentStatus.RequiresAction]: new Set([
		DomesticPaymentIntentStatus.Processing,
		DomesticPaymentIntentStatus.Paid,
		DomesticPaymentIntentStatus.Expired,
		DomesticPaymentIntentStatus.Closed,
		DomesticPaymentIntentStatus.Failed,
		DomesticPaymentIntentStatus.Unknown,
	]),
	[DomesticPaymentIntentStatus.Processing]: new Set([
		DomesticPaymentIntentStatus.Paid,
		DomesticPaymentIntentStatus.Closed,
		DomesticPaymentIntentStatus.Failed,
		DomesticPaymentIntentStatus.Unknown,
	]),
	[DomesticPaymentIntentStatus.Paid]: new Set([
		DomesticPaymentIntentStatus.Fulfilled,
		DomesticPaymentIntentStatus.Refunding,
		DomesticPaymentIntentStatus.Unknown,
	]),
	[DomesticPaymentIntentStatus.Fulfilled]: new Set([
		DomesticPaymentIntentStatus.Refunding,
		DomesticPaymentIntentStatus.Unknown,
	]),
	[DomesticPaymentIntentStatus.Refunding]: new Set([
		DomesticPaymentIntentStatus.Refunded,
		DomesticPaymentIntentStatus.Failed,
		DomesticPaymentIntentStatus.Unknown,
	]),
	[DomesticPaymentIntentStatus.Expired]: new Set([
		DomesticPaymentIntentStatus.Unknown,
	]),
	[DomesticPaymentIntentStatus.Closed]: new Set([
		DomesticPaymentIntentStatus.Unknown,
	]),
	[DomesticPaymentIntentStatus.Failed]: new Set([
		DomesticPaymentIntentStatus.Unknown,
	]),
	[DomesticPaymentIntentStatus.Refunded]: new Set([
		DomesticPaymentIntentStatus.Unknown,
	]),
	[DomesticPaymentIntentStatus.Unknown]: new Set([
		DomesticPaymentIntentStatus.RequiresAction,
		DomesticPaymentIntentStatus.Processing,
		DomesticPaymentIntentStatus.Paid,
		DomesticPaymentIntentStatus.Fulfilled,
		DomesticPaymentIntentStatus.Expired,
		DomesticPaymentIntentStatus.Closed,
		DomesticPaymentIntentStatus.Failed,
		DomesticPaymentIntentStatus.Refunding,
		DomesticPaymentIntentStatus.Refunded,
	]),
};

export function canTransitionDomesticPaymentIntent(
	from: DomesticPaymentIntentStatus,
	to: DomesticPaymentIntentStatus,
): boolean {
	if (from === to) {
		return true;
	}

	return allowedDomesticPaymentIntentTransitions[from].has(to);
}

export type DomesticPaymentProviderAction = z.infer<
	typeof DomesticPaymentProviderActionSchema
>;
